// The domains a group's agents tried and were refused (D81), with how often and when last;
// its owner allows one, or a selection, after a confirmation that names the list it changes.
import { CheckCircle } from "@phosphor-icons/react";
import { useState } from "react";
import { Button, Checkbox } from "rizzui";

import { formatNumber, useT } from "../i18n";
import { ago } from "../pages/format";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { type Column, DataTable } from "../ui/DataTable";
import { Mono } from "../ui/status";
import { useWideScreen } from "../ui/useWideScreen";
import type { DomainCount } from "./api";

interface Props {
  blocked: DomainCount[];
  /** Absent: read-only (not the owner, or no list to add to). */
  allow?: { listName: string; groups: number; onAllow: (domains: string[]) => Promise<void> };
}

export function BlockedTable({ blocked, allow }: Props) {
  const t = useT().network.page;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [asking, setAsking] = useState<string[] | null>(null);
  const wide = useWideScreen(640);
  const toggle = (d: string) => setSelected((s) => (s.has(d) ? new Set([...s].filter((x) => x !== d)) : new Set([...s, d])));
  const columns: Column<DomainCount>[] = [
    ...(allow
      ? [{ key: "pick", header: "", className: "w-10", cell: (r: DomainCount) => <Checkbox aria-label={t.allowLabel(r.domain)} checked={selected.has(r.domain)} onChange={() => toggle(r.domain)} /> }]
      : []),
    { key: "domain", header: t.columns.domain, sortValue: (r) => r.domain, cell: (r) => <Mono className="break-all text-[13px]">{r.domain}</Mono> },
    { key: "count", header: t.columns.count, sortValue: (r) => r.count, cell: (r) => formatNumber(r.count) },
    {
      key: "seen",
      header: t.columns.lastSeen,
      hideBelow: "sm",
      sortValue: (r) => r.last_seen_ms ?? 0,
      cell: (r) => <span className="whitespace-nowrap">{r.last_seen_ms ? ago(r.last_seen_ms) : "—"}</span>,
    },
    ...(allow
      ? [
          {
            key: "allow",
            header: "",
            className: "text-end",
            cell: (r: DomainCount) => (
              <Button size="sm" variant="outline" className="gap-1" aria-label={t.allowLabel(r.domain)} onClick={() => setAsking([r.domain])}>
                <CheckCircle className="h-4 w-4" aria-hidden /> {t.allow}
              </Button>
            ),
          },
        ]
      : []),
  ];
  return (
    <div className="space-y-3">
      {allow && selected.size > 0 ? (
        <Button className="gap-1.5" onClick={() => setAsking([...selected])}>
          <CheckCircle weight="bold" className="h-4 w-4" /> {t.allowSelected(selected.size)}
        </Button>
      ) : null}
      {wide ? (
        <DataTable label={t.blocked} columns={columns} rows={blocked} rowKey={(r) => r.domain} defaultSort={{ key: "count", dir: "desc" }} />
      ) : (
        // A phone: one row per domain, wrapping, with its count and Allow under it.
        <ul className="m-0 list-none divide-y divide-muted/70 p-0" aria-label={t.blocked}>
          {[...blocked].sort((a, b) => b.count - a.count).map((r) => (
            <li key={r.domain} className="flex items-start gap-3 py-3">
              {allow ? <Checkbox className="pt-0.5" aria-label={t.allowLabel(r.domain)} checked={selected.has(r.domain)} onChange={() => toggle(r.domain)} /> : null}
              <div className="min-w-0 flex-1">
                <Mono className="block whitespace-normal break-all text-[13px]">{r.domain}</Mono>
                <div className="mt-0.5 text-[12.5px] text-gray-500">
                  {formatNumber(r.count)} · {r.last_seen_ms ? ago(r.last_seen_ms) : "—"}
                </div>
              </div>
              {allow ? (
                <Button size="sm" variant="outline" className="shrink-0 gap-1" aria-label={t.allowLabel(r.domain)} onClick={() => setAsking([r.domain])}>
                  <CheckCircle className="h-4 w-4" aria-hidden /> {t.allow}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {allow && asking ? (
        <ConfirmDialog
          open
          tone="primary"
          title={t.allowTitle(asking.length)}
          label={t.allowTitle(asking.length)}
          confirmLabel={t.allow}
          onClose={() => setAsking(null)}
          onConfirm={async () => {
            await allow.onAllow(asking);
            setSelected((s) => new Set([...s].filter((d) => !asking.includes(d))));
          }}
        >
          <Mono className="mb-2 block whitespace-normal break-all text-[13px]">{asking.join(", ")}</Mono>
          {t.allowBody(allow.listName, allow.groups)}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
